<template>
  <div
    class="mle-temperature-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <ScatterChart
      v-bind="$attrs"
      draw-x-axis
      draw-y-axis
      draw-tool-tips
      mouse-tracking-ignore-y
      :data-sets="dataSets"
      :tick-frequency-x="ddvTickFrequencies?.x"
      :tick-frequency-y="ddvTickFrequencies?.y"
      @update:dom-state="domState = $event"
      @update:margins="margins = $event"
    >
      <template
        v-if="!dataSets.length || !dataSets.every(ds => ds.points.length > 0)"
        #background
      >
        <rect
          v-if="
            (domState?.plotWidth || 0) > 0 && (domState?.plotHeight || 0) > 0
          "
          class="plotBg"
          :x="margins?.left"
          :y="margins?.top"
          :width="domState?.plotWidth"
          :height="domState?.plotHeight"
        />
        <text fill="var(--axisLabelColor)" x="50%" y="50%" text-anchor="middle">
          No Data
        </text>
      </template>
      <template #tooltip="{ chartId, toolTipInfo }">
        <ToolTip
          :id="`ddv-tt-${chartId}`"
          :tool-tip-info="getFilteredToolTip(toolTipInfo)"
        />
      </template>
      <template
        #perSeries="{
          screenPoints,
          nearestPoint,
          xScaler,
          yScaler,
          dataSeriesNumber
        }"
      >
        <template v-if="screenPoints.length">
          <LinePlot
            :is-active="activeDataSets[dataSeriesNumber]"
            :data-series-number="dataSeriesNumber"
            :screen-points="screenPoints"
            curve-interpolator="catmull-rom"
          />
          <g
            v-if="activeDataSets[dataSeriesNumber]"
            class="operating-point"
            :data-series-number="dataSeriesNumber"
            :transform="`
            translate(
              ${xScaler.scale(
                dataSets[dataSeriesNumber].operatingPoint.point.x
              )},
              ${yScaler.scale(
                dataSets[dataSeriesNumber].operatingPoint.point.y
              )}
            )`"
          >
            <g>
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-width="2"
                fill="transparent"
              />
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-opacity="0.3"
                :stroke-width="8"
                fill="transparent"
              />
              <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
            </g>
          </g>

          <NearestPoint
            v-if="
              nearestPoint?.node &&
              !isStatic &&
              activeDataSets[dataSeriesNumber]
            "
            :data-series-number="dataSeriesNumber"
            :nearest-point="nearestPoint"
          />
        </template>
      </template>
    </ScatterChart>

    <CustomLegend
      v-if="drawLegend && activeDataSets.length > 0"
      v-model="activeDataSets"
      :model="dataSets"
      :static="isStatic"
      :horizontal="isHorizontal"
    >
      <template #item="{ index }">
        <div>
          <div class="control" :class="{ static: isStatic }">
            <Checkbox
              :input-id="`legend-item-${index}`"
              :model-value="activeDataSets[index]"
              :value="index"
              :style="
                dataSets[index].color
                  ? `--activeColor: ${dataSets[index].color}`
                  : `--activeColor: var(--plotColor${index})`
              "
              binary
              type="checkbox"
              :name="dataSets[index].title"
              @update:model-value="activeDataSets[index] = $event"
            />
            <label class="select-none" :for="`legend-item-${index}`">
              {{ dataSets[index].title }}
            </label>
          </div>
          <div class="mt-2">
            <label class="body-4 white-space-nowrap text-gray-700">
              Operating Temperature
            </label>
            <p class="mt-0 font-monospace font-semibold">
              {{
                Math.round(dataSets[index].operatingPoint.point.x * 100) / 100
              }}
              {{ targetTempUnit?.shortLabel || targetTempUnit?.label }}
            </p>
            <label class="body-4 white-space-nowrap text-gray-700"
              >Operating Current</label
            >
            <p class="mt-0 font-monospace font-semibold">
              {{
                Math.round(dataSets[index].operatingPoint.point.y * 100) / 100
              }}
              A
            </p>
          </div>
        </div>
      </template>
    </CustomLegend>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type {
  DataSet,
  ToolTipInfo,
  DOMState,
  Margins
} from "@incytestudios/ddv/types/common";
import {
  ScatterChart,
  LinePlot,
  NearestPoint,
  ToolTip
} from "@incytestudios/ddv";
import { toolTipFilter } from "@/utils/charts/utils";
import CustomLegend from "./CustomLegend.vue";
import type { Equipment, Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { OperatingPoint } from "@/components/types";
import { useMLETemperatureChartData } from "@/libs/charting/mleTemperature/useMLETemperatureChartData";
import {
  scenarioCalculationsKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

type DataSetExtra = {
  operatingPoint: OperatingPoint;
  loadRatio: number;
} & DataSet;

const props = defineProps<{
  scenarioCalculations?: ScenarioCalculations;
  equipment?: Equipment;
  scenario?: Scenario | null;
  drawLegend?: boolean;
  isHorizontal?: boolean;
  isStatic?: boolean;
}>();
const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);
const _sizing = injectStrict(sizingKey, null);
const { getTempUnit } = useScenarioUnits(undefined, _sizing);
const targetTempUnit = getTempUnit("Operating Temp.convert.F°").value;

const chartData = computed(() =>
  useMLETemperatureChartData(_calcs, targetTempUnit)
);
const dataSets = computed((): DataSetExtra[] => {
  return chartData.value ? [chartData.value.mleTemperatureDataSet] : [];
});

const domState = ref<DOMState>();
const margins = ref<Margins>();
const activeDataSets = ref(dataSets.value.map(() => true));

function getFilteredToolTip(tti: ToolTipInfo): ToolTipInfo {
  return toolTipFilter(tti, activeDataSets.value);
}
</script>

<style lang="scss" scoped>
.cable-temperature-chart {
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-horizontal {
    grid-template-columns: 1fr;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}
</style>
